import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import StandardLayout from 'components/StandardLayout';
import { Box, makeStyles, Grid } from '@material-ui/core';
import { decodeHTMLEntities, getErrorMessage } from 'utils';
import SignatureWidget from '#web-components/components/SignatureWidget';
import FlashMessage, { ViewType } from '#web-components/components/FlashMessage';
import AccountType from '#web-components/components/AccountType';
import Typography from '#web-components/components/Typography';
import Button, { ButtonVariants } from '#web-components/components/Button';
import { BusinessIcon, CitizenIcon, PointUpIcon } from '#web-components/components/Icons';
import { ReactComponent as IdGovUaLogo } from '#web-components/assets/id.gov.ua.logo.svg';
import { AuthType } from 'types/common';
import LoginSidebar from './components/LoginSidebar';

import styles from './Login.styles';

const useStyles = makeStyles(styles, { name: 'Login' });

const SIGN_WIDGET_XPATH_CONFIG = {
  changeKeyButton: 'changeKeyButton',
  signButton: 'signButton',
};

function Login() {
  const formEl = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();
  const [signWidgetLoading, setSignWidgetLoading] = useState(false);
  const [signature, onSignatureChange] = useState('');
  const [isBusiness, setIsBusiness] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(getErrorMessage(FTL_PARAMS.error));
  const dataToSign = FTL_PARAMS.nonce;
  const classes = useStyles();

  useEffect(() => {
    if (signature) {
      setSignWidgetLoading(true);
      formEl.current?.submit();
    }
  }, [formEl, signature]);

  const handleClick = useCallback(() => {
    setIsBusiness(!isBusiness);
  }, [isBusiness]);

  const handleLogInIdGovUa = useCallback(() => {
    window.location.href = decodeHTMLEntities(FTL_PARAMS.idGovUaAuthUrl);
  }, []);

  const hideErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  useEffect(() => {
    Cookies.set('DDM_CITIZEN_AUTH_IS_BUSINESS', JSON.stringify(isBusiness));
  }, [isBusiness]);

  return (
    <StandardLayout
      title={t('loginPageTitle')}
      isLoading={signWidgetLoading}
    >
      <Typography variant="h5" className={classes.accountTypeTitle}>
        {t('servicesInterested')}
      </Typography>
      <Box className={classes.accountTypeBox}>
        <AccountType
          isSelected={!isBusiness}
          onClick={handleClick}
          title={t('chips.citizen')}
          icon={<CitizenIcon variant="accentXL" className={classes.emojiIcon} />}
          dataXpath="chips.citizen"
        />
        <AccountType
          isSelected={isBusiness}
          onClick={handleClick}
          title={t('chips.business')}
          icon={<BusinessIcon size={26} className={classes.emojiIcon} />}
          className={classes.chips}
          dataXpath="chips.business"
        />
      </Box>
      <Grid container>
        <Grid item sm={8}>
          {
            errorMessage && (
              <FlashMessage message={errorMessage} status="error" />
            )
          }
          {(FTL_PARAMS.authType === AuthType.widget || !FTL_PARAMS.authType) && (
            <>
              <form method="post" action={FTL_PARAMS.loginAction.replace(/&amp;/g, '&')} ref={formEl}>
                <input type="hidden" name="signature" value={signature} />
                <input type="hidden" name="isBusiness" value={isBusiness.toString()} />
                <input type="hidden" name="credentialId" value="" />
                <input type="hidden" name="login" value="Sign+In" />
                <SignatureWidget
                  signWidgetUri={FTL_PARAMS.widgetUrl}
                  signWidgetHeight={FTL_PARAMS.widgetHeight}
                  changeKeyButtonCaption={t('signWidget.changeKeyButton')}
                  signButtonCaption={t('signWidget.signButton')}
                  signDataTitle=""
                  readKeyTitle=""
                  organizationCaption={t('signWidget.organization')}
                  rnokppCaption={t('signWidget.rnokpp')}
                  edrpouCaption={t('signWidget.edrpou')}
                  xpathConfig={SIGN_WIDGET_XPATH_CONFIG}
                  dataToSign={dataToSign}
                  onSignText={onSignatureChange}
                  onLoadingChange={setSignWidgetLoading}
                  onSignKeyReady={hideErrorMessage}
                  errorButtonCaption={t('signWidget.errorButtonText')}
                  qrCode={{
                    title: t('signWidget.qrCode.title'),
                    description: t('signWidget.qrCode.description'),
                    timerText: t('signWidget.qrCode.timerText'),
                    errorMessage: t('signWidget.qrCode.errorMessage'),
                  }}
                />
              </form>
              <Box className={classes.hint}>
                <FlashMessage
                  status="info"
                  customIcon={PointUpIcon}
                  message={(
                    <Typography variant="textSmall" className={classes.hintText}>
                      {t('hint')}
                      <a href={FTL_PARAMS.esignHintUrl}>{FTL_PARAMS.esignHintUrl}</a>
                    </Typography>
                  )}
                  viewType={ViewType.plain}
                />
              </Box>
            </>
          )}
          {(FTL_PARAMS.authType === AuthType.platformIdGovUa || FTL_PARAMS.authType === AuthType.registryIdGovUa) && (
            <Box className={classes.idGovUa}>
              <Box className={classes.container}>
                <Button variant={ButtonVariants.text} className={classes.btn} onClick={handleLogInIdGovUa}>
                  {t('idGovUa.logInWith')}
                  <IdGovUaLogo className={classes.btnLogo} />
                </Button>
                <Typography variant="textTiny">{t('idGovUa.description')}</Typography>
              </Box>
              <Box className={classes.hint}>
                <FlashMessage
                  status="info"
                  customIcon={PointUpIcon}
                  message={(
                    <Typography variant="textRegular" className={classes.hintText}>
                      {t('idGovUa.text')}
                    </Typography>
                  )}
                  viewType={ViewType.plain}
                />
              </Box>
            </Box>
          )}
        </Grid>
        <Grid item sm={4}>
          <div className={classes.sidebarContainer}>
            <LoginSidebar />
          </div>
        </Grid>
      </Grid>

    </StandardLayout>
  );
}

export default Login;
